import { useState } from 'react';

import type { AppNotification } from '../../api/monitor';
import type { NotificationsController } from '../../hooks/useNotifications';
import {
  notificationBackground,
  notificationDot,
  notificationTime,
  unreadBadge,
} from '../../lib/notifications';
import { NavIcon } from './NavIcon';

const BELL_ICON = 'M6 16v-5a6 6 0 0 1 12 0v5l2 3H4zM10 21h4';

/** Quantas cabem no balão; o resto está na tela de Notificações. */
const PREVIEW = 5;

interface NotificationBellProps {
  controller: NotificationsController;
  /** Leva para a tela de Notificações a partir do rodapé do balão. */
  onOpenAll: () => void;
}

/** Uma linha do balão — o mesmo desenho da tela, em tamanho menor. */
function BellRow({
  notification,
  onClick,
}: {
  notification: AppNotification;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-surface-50 hover:bg-brand-100/25 flex w-full items-start gap-2.5 border-b px-3.5 py-2.5 text-left ${notificationBackground(
        notification,
      )}`}
    >
      <span className={`mt-[5px] size-[7px] shrink-0 rounded-full ${notificationDot(notification)}`} />

      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-extrabold">{notification.title}</div>
        <div className="text-ink-600 mt-px text-[11.5px]">{notification.body}</div>
      </div>

      <span className="text-ink-400 shrink-0 text-[10.5px]">
        {notificationTime(notification.createdAt)}
      </span>
    </button>
  );
}

/**
 * Central de notificações do cabeçalho.
 *
 * Abrir o balão é o que marca as notificações como vistas: quem abriu já as
 * leu, e exigir um segundo clique para apagar o contador só faria o número
 * ficar aceso sobre avisos que a pessoa acabou de ler.
 *
 * O que estava não lido continua destacado enquanto o balão está aberto — a
 * marcação vale para o contador, não para a lista que está debaixo dos olhos.
 */
export function NotificationBell({ controller, onOpenAll }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  /**
   * O que estava por ler no instante da abertura.
   *
   * A marcação apaga o contador na hora, mas quem abriu ainda precisa ver
   * *quais* eram as novas: sem esta foto, o balão abriria com tudo já cinza e o
   * destaque teria durado menos que o piscar de olhos.
   */
  const [highlighted, setHighlighted] = useState<Set<number>>(new Set());

  const notifications = controller.state?.notifications ?? [];
  const unread = controller.state?.unread ?? 0;

  const toggle = () => {
    const next = !open;
    setOpen(next);

    // Só ao abrir: fechar não pode "desver" nada.
    if (next) {
      setHighlighted(new Set(notifications.filter((item) => !item.read).map((item) => item.id)));
      void controller.markSeen();
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={toggle}
        title={unread ? `${unread} notificação(ões) não lida(s)` : 'Notificações'}
        aria-expanded={open}
        className="text-ink-600 hover:text-navy-800 relative block p-1.5"
      >
        <NavIcon path={BELL_ICON} size={21} />

        {unread > 0 ? (
          <span className="bg-warn-700 absolute top-0.5 right-0 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9.5px] font-extrabold text-white">
            {unreadBadge(unread)}
          </span>
        ) : null}

        <span className="sr-only">Notificações</span>
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />

          <div className="animate-rise-fast border-line-200 shadow-popover absolute top-[calc(100%+10px)] -right-10 z-40 w-[min(340px,calc(100vw-32px))] overflow-hidden rounded-2xl border bg-white">
            <div className="border-surface-200 flex items-center gap-2 border-b px-3.5 py-3">
              <div className="flex-1 text-[13px] font-extrabold">Notificações</div>
              {notifications.length > PREVIEW ? (
                <span className="text-ink-400 text-[11px] font-bold">
                  {PREVIEW} de {notifications.length}
                </span>
              ) : null}
            </div>

            <div className="max-h-[300px] overflow-auto">
              {controller.error ? (
                <div className="text-danger-600 px-3.5 py-4 text-[12px] font-semibold">
                  {controller.error}
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-ink-600 px-3.5 py-4 text-[12.5px]">
                  {controller.loading ? 'Carregando…' : 'Nada aconteceu por aqui ainda.'}
                </div>
              ) : (
                notifications.slice(0, PREVIEW).map((notification) => (
                  <BellRow
                    key={notification.id}
                    notification={{
                      ...notification,
                      read: notification.read && !highlighted.has(notification.id),
                    }}
                    onClick={() => {
                      setOpen(false);
                      onOpenAll();
                    }}
                  />
                ))
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenAll();
              }}
              className="border-surface-200 bg-surface-100 text-navy-800 hover:bg-brand-100/30 w-full border-t p-3 text-[12.5px] font-extrabold"
            >
              Ver todas as notificações
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
